// See: backend/docs/arvento-telemetry.md §11
// Filodaki tüm aktif araçları Google Maps üzerinde gösterir.
// Sol panel: araç listesi (arama + tıkla → harita o araca odaklan).
// Sağ panel: harita + her araç için durum-renkli marker.

import React, { useMemo, useRef, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import GoogleMapReact from "google-map-react";

import { TELEMETRY_THRESHOLDS } from "@routebot/constants";

import LoadingSpinner from "shared/components/UIElements/LoadingSpinner";
import VehicleTelemetryMarker from "./VehicleTelemetryMarker";
import useTelemetryLive from "./hooks/useTelemetryLive";
import useTelemetryAlertCount from "./hooks/useTelemetryAlertCount";
import CriticalAlertBadge from "./components/CriticalAlertBadge";
import { snapshotDisplayPlate, snapshotMatchesPlateSearch } from "./util/match-plate-search";

import "./LiveFleetMap.css";

const DEFAULT_CENTER = { lat: 41.015137, lng: 28.97953 }; // İstanbul
const OVERSPEED_KMH = TELEMETRY_THRESHOLDS?.OVERSPEED_THRESHOLD_KMH || 100;

const getMapOptions = () => ({
  streetViewControl: false,
  scaleControl: true,
  fullscreenControl: true,
  gestureHandling: "greedy",
  minZoom: 5,
  maxZoom: 20,
  styles: [
    { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
  ],
});

const formatAge = (ageSeconds) => {
  if (ageSeconds === undefined || ageSeconds === null) return "—";
  if (ageSeconds < 60) return `${ageSeconds} sn`;
  if (ageSeconds < 3600) return `${Math.floor(ageSeconds / 60)} dk`;
  if (ageSeconds < 86400) return `${Math.floor(ageSeconds / 3600)} sa`;
  return `${Math.floor(ageSeconds / 86400)} gün`;
};

const STATUS_PRIORITY = { ALARMED: 0, MOVING: 1, IDLING: 2, PARKED: 3, OFFLINE: 4 };

const LiveFleetMap = () => {
  const { t } = useTranslation();
  const { snapshots, isLoading, error, lastLoadedAt } = useTelemetryLive({ enabled: true });
  const { count: criticalAlertCount } = useTelemetryAlertCount({ enabled: true });

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const mapRef = useRef(null);
  const mapsRef = useRef(null);
  const hasFittedRef = useRef(false);

  const validSnapshots = useMemo(
    () => snapshots.filter((s) => s.location?.lat && s.location?.lng),
    [snapshots],
  );

  // Durum bazlı sayımlar (filtre butonlarında rozet olarak gösterilir)
  const statusCounts = useMemo(() => {
    const c = { ALL: validSnapshots.length, MOVING: 0, IDLING: 0, PARKED: 0, OFFLINE: 0, ALARMED: 0 };
    validSnapshots.forEach((s) => {
      if (c[s.status] !== undefined) c[s.status] += 1;
    });
    return c;
  }, [validSnapshots]);

  const filteredSnapshots = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = validSnapshots;
    if (statusFilter !== "ALL") {
      list = list.filter((s) => s.status === statusFilter);
    }
    if (q) {
      list = list.filter((s) => snapshotMatchesPlateSearch(s, q));
    }
    return [...list].sort((a, b) => {
      const sa = STATUS_PRIORITY[a.status] ?? 9;
      const sb = STATUS_PRIORITY[b.status] ?? 9;
      if (sa !== sb) return sa - sb;
      return (a.plate || "").localeCompare(b.plate || "");
    });
  }, [validSnapshots, search, statusFilter]);

  const defaultCenter = useMemo(() => {
    if (validSnapshots.length === 0) return DEFAULT_CENTER;
    const first = validSnapshots[0];
    return { lat: first.location.lat, lng: first.location.lng };
  }, [validSnapshots]);

  const fitBoundsToFleet = useCallback(() => {
    if (!mapRef.current || !mapsRef.current || validSnapshots.length === 0) return;
    const bounds = new mapsRef.current.LatLngBounds();
    validSnapshots.forEach((s) => {
      bounds.extend(new mapsRef.current.LatLng(s.location.lat, s.location.lng));
    });
    mapRef.current.fitBounds(bounds);
    if (validSnapshots.length === 1) {
      mapRef.current.setZoom(14);
    }
  }, [validSnapshots]);

  const handleApiLoaded = ({ map, maps }) => {
    mapRef.current = map;
    mapsRef.current = maps;
    if (validSnapshots.length > 0) {
      fitBoundsToFleet();
      hasFittedRef.current = true;
    }
  };

  // Veri asenkron geldiğinde haritayı bir kez filoya sığdır.
  // Sonraki 15 sn'lik yenilemelerde kullanıcının zoom/pan'ini bozmamak için tekrar etmez.
  useEffect(() => {
    if (hasFittedRef.current) return;
    if (!mapRef.current || !mapsRef.current) return;
    if (validSnapshots.length === 0) return;
    fitBoundsToFleet();
    hasFittedRef.current = true;
  }, [validSnapshots, fitBoundsToFleet]);

  const handleSelectVehicle = (snapshot) => {
    setSelectedVehicleId(String(snapshot.vehicle?._id || snapshot.vehicle));
    if (mapRef.current && snapshot.location) {
      mapRef.current.panTo({ lat: snapshot.location.lat, lng: snapshot.location.lng });
      mapRef.current.setZoom(16);
    }
  };

  const isInitialLoading = isLoading && snapshots.length === 0;

  return (
    <div className="live-fleet-map">
      <div className="live-fleet-map__sidebar">
        <div className="live-fleet-map__header">
          <div className="live-fleet-map__badge-row">
            <CriticalAlertBadge count={criticalAlertCount} asLink />
          </div>
          <input
            type="text"
            placeholder="Ara…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="live-fleet-map__search"
          />
          <div className="live-fleet-map__filters">
            {["ALL", "MOVING", "IDLING", "PARKED", "OFFLINE"].map((f) => (
              <button
                key={f}
                type="button"
                className={`live-fleet-map__filter live-fleet-map__filter--${f.toLowerCase()} ${
                  statusFilter === f ? "live-fleet-map__filter--active" : ""
                }`}
                onClick={() => setStatusFilter(f)}
              >
                {f === "ALL" ? t("telemetryFilterAll") : t(`telemetryVehicleStatus_${f}`)}
                <span className="live-fleet-map__filter-count">{statusCounts[f] ?? 0}</span>
              </button>
            ))}
          </div>
          <div className="live-fleet-map__meta">
            <span>{filteredSnapshots.length} araç</span>
            {lastLoadedAt && (
              <span className="live-fleet-map__meta-sync">
                · {lastLoadedAt.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>

        {isInitialLoading ? (
          <div className="live-fleet-map__loading">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="live-fleet-map__empty live-fleet-map__empty--error">{t("telemetryLiveFetchFailed")}</div>
        ) : filteredSnapshots.length === 0 ? (
          <div className="live-fleet-map__empty">{t("telemetryNoVehicles")}</div>
        ) : (
          <ul className="live-fleet-map__list">
            {filteredSnapshots.map((s) => {
              const vid = String(s._id || s.vehicle?._id || s.vehicle || s.node);
              const isSelected = vid === selectedVehicleId;
              const isOverspeed = s.status !== "OFFLINE" && Number(s.speed) >= OVERSPEED_KMH;
              const isServiceless = !!s.serviceless;
              return (
                <li
                  key={vid}
                  className={`live-fleet-map__item live-fleet-map__item--${s.status} ${
                    isSelected ? "live-fleet-map__item--selected" : ""
                  } ${s.isStale ? "live-fleet-map__item--stale" : ""} ${
                    isOverspeed ? "live-fleet-map__item--overspeed" : ""
                  } ${isServiceless ? "live-fleet-map__item--serviceless" : ""}`}
                  onClick={() => handleSelectVehicle(s)}
                >
                  <div className="live-fleet-map__item-row">
                    <span className="live-fleet-map__plate">{snapshotDisplayPlate(s) || "—"}</span>
                    {isServiceless ? (
                      <span className="live-fleet-map__serviceless-pill">Servis dışı</span>
                    ) : (
                      <span className={`live-fleet-map__status-pill live-fleet-map__status-pill--${s.status}`}>
                        {t(`telemetryVehicleStatus_${s.status}`) || s.status}
                      </span>
                    )}
                  </div>
                  <div className="live-fleet-map__item-row live-fleet-map__item-sub">
                    <span className="live-fleet-map__speed-loc">
                      <span className={isOverspeed ? "live-fleet-map__speed--over" : undefined}>
                        {Math.round(s.speed || 0)} km/h{isOverspeed ? " ⚠" : ""}
                      </span>
                      {s.district ? (
                        <span className="live-fleet-map__location" title={s.address || ""}>
                          {" · "}
                          {s.district}
                        </span>
                      ) : null}
                    </span>
                    <span className="live-fleet-map__age">{formatAge(s.ageSeconds)}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="live-fleet-map__map">
        <GoogleMapReact
          options={getMapOptions}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({ map, maps }) => handleApiLoaded({ map, maps })}
          defaultCenter={defaultCenter}
          defaultZoom={11}
        >
          {filteredSnapshots.map((s) => (
            <VehicleTelemetryMarker
              key={String(s._id || s.vehicle?._id || s.vehicle || s.node)}
              lat={s.location.lat}
              lng={s.location.lng}
              snapshot={s}
              ageLabel={formatAge(s.ageSeconds)}
              onClick={handleSelectVehicle}
            />
          ))}
        </GoogleMapReact>
      </div>
    </div>
  );
};

export default LiveFleetMap;
